import { useChannelStore } from '@entities/channel'
import {
    createPost,
    createPostType,
    createShareLink,
    deleteFromTgPost,
    deletePost,
    deletePostTypes,
    deleteShareLink,
    editPost,
    getPost,
    getPosts,
    getPostsTypes,
    getShareLink,
    preparePostForTgShare,
    previewPost,
    publishPostNow
} from '@entities/post/api'
import { EDisplayPosts } from '@pages/scheduled-posts-page/ui/scheduled-posts-page'
import type { PaginatedList } from '@shared/api/types'
import { dayjs } from '@shared/lib/dayjs/dayjs'
import { getErrorMessage } from '@shared/lib/errors'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import type { Post } from '../types'
import type { TInitialStateShare, usePostsProps, usePostsResult } from './types'

export const usePosts = ({ state, display, adCampaignId, channelId, postId }: usePostsProps = {}): usePostsResult => {
    const queryClient = useQueryClient()
    const [sharedData, setSharedData] = useState<TInitialStateShare>({})
    const [pagination, setPagination] = useState({ page: 1, pageSize: 10 })
    const [dates, setDates] = useState({
        startDate: dayjs().startOf('week').toDate(),
        endDate: dayjs().endOf('week').toDate()
    })

    const { currentChannel } = useChannelStore(
        useShallow(({ current, setCurrent }) => ({ currentChannel: current, setCurrentChannel: setCurrent }))
    )

    const [selectedPostTypes, setSelectedPostTypes] = useState<number[]>([])

    const queryKey = [
        'posts',
        state,
        display,
        currentChannel?.name,
        pagination.page,
        pagination.pageSize,
        dates.startDate,
        dates.endDate,
        selectedPostTypes
    ]

    const adsPostsQueryKey = ['ad-posts', adCampaignId]
    const adsPostsQueryKeyTypes = ['ad-post-types', channelId]

    const postTypes = useQuery({
        queryKey: adsPostsQueryKeyTypes,
        queryFn: () => getPostsTypes(channelId)
    })

    const currentPost = useQuery({
        queryKey: ['post', postId],
        queryFn: () => {
            if (postId) {
                return getPost(postId)
            }
        },
        enabled: !!postId
    })

    const createTypeMutation = useMutation({
        mutationFn: createPostType,
        onSuccess: () => {
            // Инвалидируем запрос с ключом adsPostsQueryKeyTypes
            queryClient.invalidateQueries({
                queryKey: adsPostsQueryKeyTypes
            })
            queryClient.invalidateQueries({
                queryKey: ['channelDetails', channelId] // Используем ключ channelDetails
            })

            message.success('Тег успешно добавлен')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const deletePostTypeMutation = useMutation({
        mutationFn: deletePostTypes,
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: adsPostsQueryKeyTypes
            })
            queryClient.invalidateQueries({
                queryKey: ['channelDetails', channelId] // Используем ключ channelDetails
            })
            message.success('Тип удален')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const posts = useQuery({
        queryKey,
        queryFn: () => {
            if (!state) return
            if (display === EDisplayPosts.day) {
                return getPosts({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    startDate: dates.startDate,
                    endDate: dates.endDate,
                    state,
                    currentChannel,
                    postTypes: selectedPostTypes
                })
            }
            if (display === EDisplayPosts.week) {
                return getPosts({
                    page: 1,
                    pageSize: 1000,
                    startDate: dates.startDate,
                    endDate: dates.endDate,
                    state,
                    currentChannel,
                    postTypes: selectedPostTypes
                })
            }
            if (display === EDisplayPosts.period) {
                return getPosts({
                    page: 1,
                    pageSize: 1000,
                    startDate: dates.startDate,
                    endDate: dates.endDate,
                    state,
                    currentChannel,
                    postTypes: selectedPostTypes
                })
            }
            if (display === EDisplayPosts.all) {
                return getPosts({
                    page: pagination.page,
                    pageSize: pagination.pageSize,
                    state,
                    currentChannel,
                    postTypes: selectedPostTypes
                })
            } else if (display === EDisplayPosts.month) {
                return getPosts({
                    page: 1,
                    pageSize: 1000,
                    state,
                    startDate: dayjs().startOf('month').toDate(),
                    endDate: dayjs().endOf('month').toDate(),
                    currentChannel,
                    postTypes: selectedPostTypes
                })
            }
        },
        placeholderData: keepPreviousData,
        enabled: !!state
    })

    const adsPosts = useQuery({
        queryKey: adsPostsQueryKey,
        queryFn: () => getPosts({ page: 1, pageSize: 1000, isAdvertising: true, adCampaignId: adCampaignId }),
        placeholderData: keepPreviousData,
        enabled: !!adCampaignId
    })

    const { message } = App.useApp()

    const createPostMutation = useMutation({
        mutationFn: createPost,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Post>>({
                queryKey: ['posts'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Пост успешно создан')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const publishPostNowMutation = useMutation({
        mutationFn: publishPostNow,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Post>>({
                queryKey: ['posts'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Пост опубликован')
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const previewPostMutation = useMutation({
        mutationFn: previewPost,
        onSuccess: () => message.success('Превью поста отправлено в телеграм'),
        onError: (error) => message.error(getErrorMessage(error))
    })

    const editPostMutation = useMutation({
        mutationFn: editPost,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Post>>({
                queryKey: ['posts'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Пост сохранен')
        }
    })

    const schedulePostMutation = useMutation({
        mutationFn: editPost,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Post>>({
                queryKey: ['posts'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })
            message.success('Пост запланирован')
        }
    })

    const deletePostMutation = useMutation({
        mutationFn: deletePost,
        onSuccess: () => {
            const queries = queryClient.getQueriesData<PaginatedList<Post>>({
                queryKey: ['posts'],
                type: 'active'
            })

            queries.forEach(([query]) => {
                queryClient.invalidateQueries({
                    queryKey: query
                })
            })

            if (adCampaignId) {
                queryClient.invalidateQueries({
                    queryKey: adsPostsQueryKey
                })
            }

            message.success('Пост удален')
        }
    })

    const deleteFromTgPostMutation = useMutation({
        mutationFn: deleteFromTgPost,
        onSuccess: () => message.success('Пост успешно удален из телеграм')
    })

    const createShareLinkMutation = useMutation({
        mutationFn: createShareLink,
        onSuccess: (data) => {
            setSharedData({
                id: data.id,
                sharedId: data.sharedId,
                isShared: true
            })
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const getShareLinkMutation = useMutation({
        mutationFn: getShareLink
    })

    const deleteShareLinkMutation = useMutation({
        mutationFn: () => deleteShareLink({ id: sharedData.id, sharedId: sharedData.sharedId }),
        onSuccess: () => {
            clearStateShare()
        },
        onError: (error) => message.error(getErrorMessage(error))
    })

    const clearStateShare = () => {
        setSharedData({
            id: sharedData.id
        })
    }

    const preparePostForTgShareMutation = useMutation({
        mutationFn: preparePostForTgShare
    })

    const enrichedPosts = useMemo(() => {
        if (!posts.data || !postTypes.data) return posts.data // Возвращаем исходные данные, если они отсутствуют

        return {
            ...posts.data, // Копируем все поля из posts.data
            results: posts.data.results.map((post) => {
                return {
                    ...post,
                    postType: postTypes?.data?.find((type) => type.id === post?.postType.value)
                }
            })
        }
    }, [posts.data, postTypes.data])

    return {
        posts: enrichedPosts,
        currentPost: currentPost.data,
        adsPosts: adsPosts.data,
        postTypes: postTypes.data,
        selectedPostTypes,
        sharedData,
        pagination,
        dates,
        isPostsLoading: posts.isPlaceholderData,
        isAdsPostsLoading: adsPosts.isPlaceholderData,
        createPostIsLoading: createPostMutation.isPending,
        createTypeIsLoading: createTypeMutation.isPending,
        publishPostIsLoading: publishPostNowMutation.isPending,
        deletePostIsLoading: deletePostMutation.isPending,
        deleteTypeIsLoading: deletePostTypeMutation.isPending,
        editPostIsLoading: editPostMutation.isPending,
        previewPostIsLoading: previewPostMutation.isPending,
        actions: {
            setSharedData,
            setPagination,
            setDates,
            clearStateShare,
            setSelectedPostTypes,
            createPost: createPostMutation.mutateAsync,
            createType: createTypeMutation.mutateAsync,
            deleteType: deletePostTypeMutation.mutateAsync,
            publishPostNow: publishPostNowMutation.mutateAsync,
            deletePost: deletePostMutation.mutateAsync,
            editPost: editPostMutation.mutateAsync,
            schedulePost: schedulePostMutation.mutateAsync,
            previewPost: previewPostMutation.mutateAsync,
            deleteFromTgPost: deleteFromTgPostMutation.mutateAsync,
            createShareLink: createShareLinkMutation.mutateAsync,
            getShareLink: getShareLinkMutation.mutateAsync,
            deleteShareLink: deleteShareLinkMutation.mutateAsync,
            preparePostForTgShare: preparePostForTgShareMutation.mutateAsync
        }
    }
}
